<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from "vue";
import Swiper from "swiper";
import "swiper/css";
import "swiper/css/navigation";




const props = defineProps({
  place: { type: Object, required: true },
});

const { getDistance } = useDistance()


const swiperContainer = ref(null);

// Compute grouped images for slides
const groupedImages = computed(() => {
  if (!props.place.images || props.place.images.length <= 1) return [];

  const images = props.place.images.slice(1); // Exclude first image
  const grouped = [];
  for (let i = 0; i < images.length; i += 2) {
    grouped.push(images.slice(i, i + 2)); // Group images in pairs
  }
  return grouped;
});




const contentType = ref('')

// 1. Fetch headers on mount
onMounted(async () => {
  
})

// 2. Decide which tag to use
const viewerTag = computed(() => {
  if (isPdf(props.place.menu)) return 'iframe'
  if (isImage(props.place.menu)) return 'img'
  return null
})


const sortedBranches = computed(() => {
  console.log('props.place.branches',props.place.branches);
  
  // First, get the filtered list (or all branches if you want)
  const list = props.place.branches.map(branch => {
    const dist = getDistance(branch.location.latitude, branch.location.longitude) || Infinity;
    return { branch, dist };
  });
  // Sort by the numeric distance
  list.sort((a, b) => a.dist - b.dist);
  // Return just the branches in sorted order
  return list.map(item => item.branch);
});


function isPdf(url) {
    return /\.pdf$/i.test(url);
}

function isImage(url) {
  return (
    /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(url)
  )
}



const format12 = computed(() => (
      time24: string
  ) => {
    // Build a Date from today + the supplied time
    const [h, m] = time24.split(':').map(Number)
      const dt = new Date()
      dt.setHours(h, m)


      // Use Arabic (SA) locale, 12-hour clock
      return new Intl.DateTimeFormat('ar-SA-u-nu-latn', {
        hour:   '2-digit',
        minute: '2-digit',
        hour12: true
      }).format(dt)
  })

/**
 * Build a Google Maps link centered on given coordinates.
 * @param lat Latitude (e.g. 30.0444)
 * @param lng Longitude (e.g. 31.2357)
 * @param useSearchUrl If true, uses the new /maps/search URL; otherwise /?q format
 */
const mapLinkBuilder = computed(() => (
  lat: number,
  lng: number,
  useSearchUrl = true
) => {
  const coords = `${lat},${lng}`
  return useSearchUrl
    ? `https://www.google.com/maps/search/?api=1&query=${coords}`
    : `https://maps.google.com/?q=${coords}`
})


const searchQuery = ref('')

// computed array filtered by searchQuery
const filteredBranches = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return sortedBranches.value
  return sortedBranches.value.filter(branch => {
    console.log('branch',branch);
    
    return (
      branch.city_name?.toLowerCase().includes(q) ||
      branch.neighborhood?.toLowerCase().includes(q) ||
      branch.street?.toLowerCase().includes(q)
    )
  })
})



// Initialize Swiper and Fancybox
onMounted(() => {
  nextTick(() => {
    new Swiper(".place_gallery_wrapper .swiper-container", {
      slidesPerView: 5.5, // Show one slide at a time (to match custom layout)
      spaceBetween: 10,

      loop: false,
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
      breakpoints: {
        320: { slidesPerView: 2.5, spaceBetween: 10 },
        480: { slidesPerView: 2.5, spaceBetween: 10 },
        640: { slidesPerView: 3.5, spaceBetween: 20 },
        991: { slidesPerView: 6.5, spaceBetween: 10 },
      },
    });


  });
});
</script>


<template>

  <div v-if="place.images && place.images.length" class="place_gallery_wrapper container">
    <ul class="nav nav-tabs profile-tabs-wrapper" id="placeTab" role="tablist">
      <li class="nav-item" role="presentation">
        <button class="nav-link active" id="images-tab" data-bs-toggle="tab" data-bs-target="#images" type="button"
          role="tab" aria-controls="images" aria-selected="true">
          <svg id="gallery_linear" data-name="gallery/linear" xmlns="http://www.w3.org/2000/svg" width="14" height="14"
            viewBox="0 0 14 14">
            <g id="vuesax_linear_gallery" data-name="vuesax/linear/gallery">
              <g id="gallery">
                <path id="Vector"
                  d="M4.083,11.667h3.5c2.917,0,4.083-1.167,4.083-4.083v-3.5C11.667,1.167,10.5,0,7.583,0h-3.5C1.167,0,0,1.167,0,4.083v3.5C0,10.5,1.167,11.667,4.083,11.667Z"
                  transform="translate(1.167 1.167)" fill="none" stroke="currentColor" stroke-linecap="round"
                  stroke-linejoin="round" stroke-width="1.2" />
                <path id="Vector-2" data-name="Vector"
                  d="M2.333,1.167A1.167,1.167,0,1,1,1.167,0,1.167,1.167,0,0,1,2.333,1.167Z"
                  transform="translate(4.083 3.5)" fill="none" stroke="currentColor" stroke-linecap="round"
                  stroke-linejoin="round" stroke-width="1.2" />
                <path id="Vector-3" data-name="Vector"
                  d="M0,4.056,2.876,2.125a1.314,1.314,0,0,1,1.54.082l.193.169a1.3,1.3,0,0,0,1.645,0L8.68.293a1.3,1.3,0,0,1,1.645,0l.951.817"
                  transform="translate(1.558 6.999)" fill="none" stroke="currentColor" stroke-linecap="round"
                  stroke-linejoin="round" stroke-width="1.2" />
                <path id="Vector-4" data-name="Vector" d="M0,0H14V14H0Z" fill="none" opacity="0" />
              </g>
            </g>
          </svg>

          الصور
        </button>
      </li>
      <li v-if="place.branches.length > 0" class="nav-item" role="presentation">
        <button class="nav-link" id="branches-tab" data-bs-toggle="tab" data-bs-target="#branches" type="button"
          role="tab" aria-controls="branches" aria-selected="false">
          <svg id="location_linear" data-name="location/linear" xmlns="http://www.w3.org/2000/svg" width="14"
            height="14" viewBox="0 0 14 14">
            <g id="vuesax_linear_location" data-name="vuesax/linear/location">
              <g id="location">
                <path id="Vector" d="M3.64,1.82A1.82,1.82,0,1,1,1.82,0,1.82,1.82,0,0,1,3.64,1.82Z"
                  transform="translate(5.18 4.194)" fill="none" stroke="currentColor" stroke-width="1.1" />
                <path id="Vector-2" data-name="Vector"
                  d="M.142,3.786c1.149-5.052,8.633-5.046,9.777.006.671,2.963-1.172,5.472-2.788,7.023a3.03,3.03,0,0,1-4.206,0C1.314,9.263-.529,6.749.142,3.786Z"
                  transform="translate(1.97 1.167)" fill="none" stroke="currentColor" stroke-width="1.1" />
                <path id="Vector-3" data-name="Vector" d="M0,0H14V14H0Z" transform="translate(14 14) rotate(180)"
                  fill="none" opacity="0" />
              </g>
            </g>
          </svg>

          الفروع
        </button>
      </li>
      <li v-if="place.has_menu" class="nav-item" role="presentation">
        <button class="nav-link" id="menu-tab" data-bs-toggle="tab" data-bs-target="#menu" type="button" role="tab"
          aria-controls="menu" aria-selected="false">
          <svg id="receipt-edit_linear" data-name="receipt-edit/linear" xmlns="http://www.w3.org/2000/svg" width="15"
            height="15" viewBox="0 0 15 15">
            <g id="vuesax_linear_receipt-edit" data-name="vuesax/linear/receipt-edit">
              <path id="Vector"
                d="M10.625,5.812V3.15C10.625.631,10.037,0,7.675,0H2.95C.588,0,0,.631,0,3.15v7.037c0,1.663.913,2.056,2.019.869l.006-.006a1.1,1.1,0,0,1,1.737.094l.631.844"
                transform="translate(2.188 1.25)" fill="none" stroke="currentColor" stroke-linecap="round"
                stroke-linejoin="round" stroke-width="1.1" />
              <path id="Vector-2" data-name="Vector" d="M0,0H5" transform="translate(5 4.375)" fill="none"
                stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.1" />
              <path id="Vector-3" data-name="Vector" d="M0,0H3.75" transform="translate(5.625 6.875)" fill="none"
                stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.1" />
              <g id="Group" transform="translate(8.857 8.873)">
                <path id="Vector-4" data-name="Vector"
                  d="M2.524.358.312,2.571a.77.77,0,0,0-.188.369l-.119.844a.4.4,0,0,0,.475.475l.844-.119a.743.743,0,0,0,.369-.188L3.906,1.739a.853.853,0,0,0,0-1.388A.85.85,0,0,0,2.524.358Z"
                  transform="translate(0 0)" fill="none" stroke="currentColor" stroke-linecap="round"
                  stroke-linejoin="round" stroke-width="1.1" />
                <path id="Vector-5" data-name="Vector" d="M0,0A1.991,1.991,0,0,0,1.388,1.388"
                  transform="translate(2.205 0.677)" fill="none" stroke="currentColor" stroke-linecap="round"
                  stroke-linejoin="round" stroke-width="1.1" />
              </g>
              <path id="Vector-6" data-name="Vector" d="M0,0H15V15H0Z" fill="none" opacity="0" />
            </g>
          </svg>
          {{ place.menu_text ?? 'المنيو'}}
        </button>
      </li>
      <li v-if="place.questions && place.questions.length" class="nav-item" role="presentation">
        <button class="nav-link" id="faq-tab" data-bs-toggle="tab" data-bs-target="#faq" type="button" role="tab"
          aria-controls="faq" aria-selected="false">
          <svg id="messages-3_linear" data-name="messages-3/linear" xmlns="http://www.w3.org/2000/svg" width="15"
            height="15" viewBox="0 0 15 15">
            <g id="vuesax_linear_messages-3" data-name="vuesax/linear/messages-3">
              <g id="messages-3">
                <path id="Vector"
                  d="M10.625,2.656V5.844a2.641,2.641,0,0,1-.731,1.925A2.641,2.641,0,0,1,7.969,8.5V9.631a.531.531,0,0,1-.825.444l-.606-.4a2.268,2.268,0,0,0,.081-.631V6.5A2.008,2.008,0,0,0,4.494,4.375H.25c-.087,0-.169.006-.25.013V2.656A2.51,2.51,0,0,1,2.656,0H7.969A2.51,2.51,0,0,1,10.625,2.656Z"
                  transform="translate(3.125 1.25)" fill="none" stroke="currentColor" stroke-linecap="round"
                  stroke-linejoin="round" stroke-width="1.1" />
                <path id="Vector-2" data-name="Vector"
                  d="M8.494,2.125V4.669a2.268,2.268,0,0,1-.081.631A1.972,1.972,0,0,1,6.369,6.794h-1.7L2.781,8.05a.419.419,0,0,1-.656-.35V6.794A2.124,2.124,0,0,1,.588,6.213,2.127,2.127,0,0,1,0,4.669V2.125A1.989,1.989,0,0,1,1.875.013C1.956.006,2.037,0,2.125,0H6.369A2.008,2.008,0,0,1,8.494,2.125Z"
                  transform="translate(1.25 5.625)" fill="none" stroke="currentColor" stroke-linecap="round"
                  stroke-linejoin="round" stroke-width="1.1" />
                <path id="Vector-3" data-name="Vector" d="M0,0H15V15H0Z" fill="none" opacity="0" />
              </g>
            </g>
          </svg>

          أسئلة و أجوبة
        </button>
      </li>
    </ul>

    <div class="tab-content profile-tabs-content" id="placeTabContent">
      <div class="tab-pane fade show active" id="images" role="tabpanel" aria-labelledby="images-tab">
        <div class="place-gallery-images">
          <div class="place-gallery-content">
            <div class="swiper-container" ref="swiperContainer">
              <div class="swiper-wrapper">
                <!-- First Image (Full Width) -->
                <div class="swiper-slide main-image" v-if="place.images.length">
                  <a :href="place.images[0].large" data-fancybox="place-gallery" class="slide_item">
                    <img :src="place.images[0].medium" :alt="place.name" loading="lazy" />
                  </a>
                </div>

                <!-- Remaining Images (2-Column Grid) -->
                <div v-for="(group, groupIndex) in groupedImages" :key="groupIndex" class="swiper-slide grid-images">
                  <div class="grid-container">
                    <div v-for="(image, index) in group" :key="index" class="grid-item">
                      <a :href="image.large" data-fancybox="place-gallery" class="slide_item">
                        <img :src="image.medium" :alt="place.name" loading="lazy" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Navigation buttons -->
              <div class="swiper-button-next"></div>
              <div class="swiper-button-prev"></div>
            </div>
          </div>
        </div>
      </div>
      <div class="tab-pane fade" v-if="sortedBranches.length > 0" id="branches" role="tabpanel"
        aria-labelledby="branches-tab">
     

        <div v-for="(branch, index) in sortedBranches.slice(0, 3)" :key="index" class="branch-item">
            <!-- Toggle -->
            <a class="branch-item-name" data-bs-toggle="collapse" :href="'#collapse' + index" role="button"
            :aria-expanded="index === 0 ? 'true' : 'false'" :aria-controls="'collapse' + index">
              <span class="branch-item-title">
                {{ branch.city_name }} –
                {{ branch.neighborhood }} –
                {{ branch.street }}
              </span>
              <span class="branch-item-status " :class="{ open: branch.status === 1, close: branch.status != 1 }">
                <span v-if="branch.available_all_days == true">مفتوح طوال الاسبوع</span>
                <span v-else-if="branch.status === 1">مفتوح</span>
                <span v-else-if="branch.status === 2">مغلق</span>
                <span v-else-if="branch.status === 3">قارب على الإغلاق</span>
              </span>
            </a>

            <!-- Collapsible content -->
            <div class="collapse " :class="{ show: index < 1 }" v-if="branch.working_days_and_time.length"
              :id="'collapse' + index">
              <div class="branch-item-content">

                <!-- Working days & times -->
                <div class="working-days">
                  <div class="working-day" v-for="(day, dayIndex) in branch.working_days_and_time" :key="dayIndex">
                    <div class="day">{{ day.label }}</div>
                    <div class="time">
                      <span v-if="branch.available_all_days == true">مفتوح 24 ساعة</span>
                      <span v-else-if="!day.active">مغلق</span>
                      <span v-else-if="day.full_time">مفتوح 24 ساعة</span>
                      <span v-else v-for="(duration, durationIndex) in day.durations" :key="durationIndex">
                        {{ format12(duration.from) }} – {{ format12(duration.to) }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Action buttons -->
                <div class="branch-options">
                  <a :href="mapLinkBuilder(branch.location.latitude, branch.location.longitude, false)" target="_blank"
                    class="branch-location">
                    <!-- location SVG -->
                    <svg id="vuesax_linear_location" data-name="vuesax/linear/location"
                      xmlns="http://www.w3.org/2000/svg" width="11.734" height="11.734" viewBox="0 0 11.734 11.734">
                      <g id="location">
                        <path id="Vector" d="M3.051,1.525A1.525,1.525,0,1,1,1.525,0,1.525,1.525,0,0,1,3.051,1.525Z"
                          transform="translate(4.342 3.515)" fill="none" stroke="#000" stroke-width="1.1" />
                        <path id="Vector-2" data-name="Vector"
                          d="M.119,3.173c.963-4.234,7.236-4.229,8.194,0,.562,2.484-.983,4.586-2.337,5.887a2.539,2.539,0,0,1-3.525,0C1.1,7.764-.444,5.657.119,3.173Z"
                          transform="translate(1.651 0.978)" fill="none" stroke="#000" stroke-width="1.1" />
                        <path id="Vector-3" data-name="Vector" d="M0,0H11.734V11.734H0Z"
                          transform="translate(11.734 11.734) rotate(180)" fill="none" opacity="0" />
                      </g>
                    </svg>
                    <span v-if="getDistance(branch.location.latitude, branch.location.longitude)" class="branch-km">{{
                      getDistance(branch.location.latitude, branch.location.longitude).toFixed(2) }}
                      km</span>
                    اذهب إلى الفرع
                  </a>

                  <a v-if="branch.phone_number" href="#" class="branch-call">
                    <!-- call SVG -->
                    <svg id="vuesax_linear_call" data-name="vuesax/linear/call" xmlns="http://www.w3.org/2000/svg"
                      width="10.972" height="10.972" viewBox="0 0 10.972 10.972">
                      <g id="call">
                        <g id="call-2" data-name="call" transform="translate(0.914 0.914)">
                          <path id="Vector"
                            d="M9.13,7.466a1.164,1.164,0,0,1-.114.5A1.886,1.886,0,0,1,8.7,8.43a2.061,2.061,0,0,1-.75.539,2.3,2.3,0,0,1-.892.174,3.81,3.81,0,0,1-1.49-.334A8.03,8.03,0,0,1,4,7.9a13.144,13.144,0,0,1-1.5-1.28A12.99,12.99,0,0,1,1.225,5.13a8.151,8.151,0,0,1-.9-1.559A3.851,3.851,0,0,1,0,2.076a2.365,2.365,0,0,1,.165-.882A2.1,2.1,0,0,1,.69.43,1.34,1.34,0,0,1,1.641,0a.859.859,0,0,1,.37.082.745.745,0,0,1,.306.256L3.379,1.833a1.565,1.565,0,0,1,.183.32.723.723,0,0,1,.064.279.621.621,0,0,1-.1.325,1.555,1.555,0,0,1-.256.325l-.347.361a.245.245,0,0,0-.073.183.415.415,0,0,0,.014.105c.014.037.027.064.037.091a3.794,3.794,0,0,0,.425.585c.206.238.425.48.663.722s.485.466.727.672a3.509,3.509,0,0,0,.59.421c.023.009.05.023.082.037a.316.316,0,0,0,.114.018A.252.252,0,0,0,5.692,6.2l.347-.343A1.4,1.4,0,0,1,6.369,5.6a.609.609,0,0,1,.325-.1.73.73,0,0,1,.279.059,1.768,1.768,0,0,1,.32.178L8.805,6.817a.693.693,0,0,1,.251.293A.938.938,0,0,1,9.13,7.466Z"
                            fill="none" stroke="#000" stroke-width="1.1" />
                        </g>
                        <path id="Vector-2" data-name="Vector" d="M0,0H10.972V10.972H0Z" fill="none" opacity="0" />
                      </g>
                    </svg>
                    <span>{{ branch.phone_number }}</span>
                  </a>
                </div>
                </div>
              </div>
            </div>


        <div v-if="place.branches.length > 3" class="moreLoading">
          <a href="javascript:;" class="section_subtitle load-more has-spinner-link" data-bs-toggle="modal"
            data-bs-target="#branches_modal">
            <div class="button_text">عرض المزيد</div>
          </a>
        </div>

      </div>
      <div class="tab-pane fade" v-if="place.has_menu" id="menu" role="tabpanel"
        aria-labelledby="menu-tab">


        <div class="branch-menu">




          <iframe
            v-if="viewerTag == 'iframe'"
            :src="`${place.menu}?v=5#toolbar=1&navpanes=0&scrollbar=0&view=FitH&zoom=100`"
            width="100%"
            height="500px"
            style="border: none;border-radius: 18px;"
            :title="place.name"
            allowfullscreen
            ref="pdfIframe"
            ></iframe>
            

          <a
              v-else-if="viewerTag == 'img'"
              :href="place.menu"
              data-fancybox="place-menu"
            >
              <img
                :src="place.menu"
                :alt="place.name"
                loading="lazy"
              />
            </a>
         
        </div>
      </div>
      <div class="tab-pane fade" id="faq" role="tabpanel" aria-labelledby="faq-tab">
        <div class="faqs-place-wrapper">
          <div v-for="(question, questionIndex) in place.questions" :key="questionIndex" class="question-item">
            <h3 class="question">
              {{ question.content }}
            </h3>
            <p class="answer">
              {{ question.answer }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="modal bottomSheet userModels branchesModal" id="branches_modal"
    tabindex="-1" aria-labelledby="filterModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="back_wrapper">
            <button type="button"  aria-label="Close" data-bs-dismiss="modal" class="backIcon">
              <i class="fi-rr-angle-right"></i>
            </button>
            <span>
              الرجوع
            </span>

          </div>
        </div>
        <!-- Modal Body -->
        <div class="modal-body pt-3">

          <div class="search_input">
            <input class="searchInput" v-model="searchQuery"  type="text" name="q" id="qM" autocomplete="off" placeholder="بحث">
            <button class="searchButton" type="button">
              <svg id="vuesax_linear_search-normal" data-name="vuesax/linear/search-normal"
                xmlns="http://www.w3.org/2000/svg" width="21.029" height="21.029" viewBox="0 0 21.029 21.029">
                <g id="search-normal">
                  <path id="Vector" d="M16.648,8.324A8.324,8.324,0,1,1,8.324,0,8.324,8.324,0,0,1,16.648,8.324Z"
                    transform="translate(1.752 1.752)" fill="none" stroke="#d0d5dc" stroke-linecap="round"
                    stroke-linejoin="round" stroke-width="1.7" />
                  <path id="Vector-2" data-name="Vector" d="M1.752,1.752,0,0" transform="translate(17.524 17.524)"
                    fill="none" stroke="#d0d5dc" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.7" />
                  <path id="Vector-3" data-name="Vector" d="M0,0H21.029V21.029H0Z" fill="none" opacity="0" />
                </g>
              </svg>
            </button>
          </div>



          <div class="modal-branches-items">
            <div v-for="(branch, index) in filteredBranches" :key="index" class="branch-item">
            <!-- Toggle -->
            <a class="branch-item-name" data-bs-toggle="collapse" :href="'#collapseModal' + index" role="button"
            :aria-expanded="index === 0 ? 'true' : 'false'" :aria-controls="'collapse' + index">
              <span class="branch-item-title">
                {{ branch.city_name }} –
                {{ branch.neighborhood }} –
                {{ branch.street }}
              </span>
              <span class="branch-item-status " :class="{ open: branch.status === 1, close: branch.status != 1 }">
                <span v-if="branch.available_all_days == true">مفتوح 24 ساعة</span>
                <span v-else-if="branch.status === 1">مفتوح</span>
                <span v-else-if="branch.status === 2">مغلق</span>
                <span v-else-if="branch.status === 3">قارب على الإغلاق</span>
              </span>
            </a>

            <!-- Collapsible content -->
            <div class="collapse " :class="{ show: index < 1 }" v-if="branch.working_days_and_time.length"
              :id="'collapseModal' + index">
              <div class="branch-item-content">

                <!-- Working days & times -->
                <div class="working-days">
                  <div class="working-day" v-for="(day, dayIndex) in branch.working_days_and_time" :key="dayIndex">
                    <div class="day">{{ day.label }}</div>
                    <div class="time">
                      <span v-if="branch.available_all_days == true">مفتوح 24 ساعة</span>
                      <span v-else-if="!day.active">مغلق</span>
                      <span v-else-if="day.full_time">مفتوح 24 ساعة</span>
                      <span v-else v-for="(duration, durationIndex) in day.durations" :key="durationIndex">
                        {{ format12(duration.from) }} – {{ format12(duration.to) }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Action buttons -->
                <div class="branch-options">
                  <a :href="mapLinkBuilder(branch.location.latitude, branch.location.longitude, false)" target="_blank"
                    class="branch-location">
                    <!-- location SVG -->
                    <svg id="vuesax_linear_location" data-name="vuesax/linear/location"
                      xmlns="http://www.w3.org/2000/svg" width="11.734" height="11.734" viewBox="0 0 11.734 11.734">
                      <g id="location">
                        <path id="Vector" d="M3.051,1.525A1.525,1.525,0,1,1,1.525,0,1.525,1.525,0,0,1,3.051,1.525Z"
                          transform="translate(4.342 3.515)" fill="none" stroke="#000" stroke-width="1.1" />
                        <path id="Vector-2" data-name="Vector"
                          d="M.119,3.173c.963-4.234,7.236-4.229,8.194,0,.562,2.484-.983,4.586-2.337,5.887a2.539,2.539,0,0,1-3.525,0C1.1,7.764-.444,5.657.119,3.173Z"
                          transform="translate(1.651 0.978)" fill="none" stroke="#000" stroke-width="1.1" />
                        <path id="Vector-3" data-name="Vector" d="M0,0H11.734V11.734H0Z"
                          transform="translate(11.734 11.734) rotate(180)" fill="none" opacity="0" />
                      </g>
                    </svg>
                    <span v-if="getDistance(branch.location.latitude, branch.location.longitude)" class="branch-km">{{
                      getDistance(branch.location.latitude, branch.location.longitude).toFixed(2) }}
                      km</span>
                    اذهب إلى الفرع
                  </a>

                  <a v-if="branch.phone_number" href="#" class="branch-call">
                    <!-- call SVG -->
                    <svg id="vuesax_linear_call" data-name="vuesax/linear/call" xmlns="http://www.w3.org/2000/svg"
                      width="10.972" height="10.972" viewBox="0 0 10.972 10.972">
                      <g id="call">
                        <g id="call-2" data-name="call" transform="translate(0.914 0.914)">
                          <path id="Vector"
                            d="M9.13,7.466a1.164,1.164,0,0,1-.114.5A1.886,1.886,0,0,1,8.7,8.43a2.061,2.061,0,0,1-.75.539,2.3,2.3,0,0,1-.892.174,3.81,3.81,0,0,1-1.49-.334A8.03,8.03,0,0,1,4,7.9a13.144,13.144,0,0,1-1.5-1.28A12.99,12.99,0,0,1,1.225,5.13a8.151,8.151,0,0,1-.9-1.559A3.851,3.851,0,0,1,0,2.076a2.365,2.365,0,0,1,.165-.882A2.1,2.1,0,0,1,.69.43,1.34,1.34,0,0,1,1.641,0a.859.859,0,0,1,.37.082.745.745,0,0,1,.306.256L3.379,1.833a1.565,1.565,0,0,1,.183.32.723.723,0,0,1,.064.279.621.621,0,0,1-.1.325,1.555,1.555,0,0,1-.256.325l-.347.361a.245.245,0,0,0-.073.183.415.415,0,0,0,.014.105c.014.037.027.064.037.091a3.794,3.794,0,0,0,.425.585c.206.238.425.48.663.722s.485.466.727.672a3.509,3.509,0,0,0,.59.421c.023.009.05.023.082.037a.316.316,0,0,0,.114.018A.252.252,0,0,0,5.692,6.2l.347-.343A1.4,1.4,0,0,1,6.369,5.6a.609.609,0,0,1,.325-.1.73.73,0,0,1,.279.059,1.768,1.768,0,0,1,.32.178L8.805,6.817a.693.693,0,0,1,.251.293A.938.938,0,0,1,9.13,7.466Z"
                            fill="none" stroke="#000" stroke-width="1.1" />
                        </g>
                        <path id="Vector-2" data-name="Vector" d="M0,0H10.972V10.972H0Z" fill="none" opacity="0" />
                      </g>
                    </svg>
                    <span>{{ branch.phone_number }}</span>
                  </a>
                </div>

              </div>
            </div>
          </div>
          </div>


        </div>
        <!-- End Modal Body -->
      </div>
    </div>
  </div>



</template>

<style scoped>
.place-gallery-wrapper {
  width: 100%;
}

.swiper-slide {
  display: flex;
  justify-content: center;
  align-items: center;
}

.main-image {
  width: 220px !important;
}

@media(max-width: 576px) {
  .main-image {
    width: 180px !important;
  }
}

.main-image img {
  width: 100% !important;
  height: 330px !important;
  border-radius: 18px !important;
}

.grid-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.grid-container .grid-item img {
  width: 100% !important;
  height: 160px !important;
  border-radius: 18px !important;
}

/* .place_gallery_wrapper {
  padding: 20px;
}

.place-gallery-content {
  position: relative;
}

.swiper-container {
  width: 100%;
  overflow: hidden;
}

.swiper-slide {
  text-align: center;
}

.swiper-slide img {
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
}

.swiper-button-next,
.swiper-button-prev {
  color: #000;
} */
</style>
